import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Typography,
} from "@material-ui/core";

import ModalSolicitud from "../components/ModalSolicitud";
import { guardarPermisosGestor } from "../services/bibliotecaApi";

/**
 * Que tipos de solicitud gestiona una persona y, dentro de cada uno, en que
 * estados puede actuar, como en AGIL. Los estados tambien deciden que ve:
 * cuando una solicitud pasa a un estado que no tiene, deja de verla.
 */
const ModalPermisos = ({ gestor, tipos, onCerrar, onGuardado, onMensaje }) => {
  // { "Bib_Lib": ["Bib_Sol_E", ...] }: un tipo presente es un tipo asignado,
  // aunque todavia no tenga estados.
  const [permisos, setPermisos] = useState(gestor.permisos ?? {});
  const [enviando, setEnviando] = useState(false);

  const alternarTipo = (codigo) =>
    setPermisos((actuales) => {
      const { [codigo]: quitado, ...resto } = actuales;
      return quitado ? resto : { ...actuales, [codigo]: [] };
    });

  const alternarEstado = (tipo, estado) =>
    setPermisos((actuales) => {
      const estados = actuales[tipo] ?? [];
      return {
        ...actuales,
        [tipo]: estados.includes(estado)
          ? estados.filter((codigo) => codigo !== estado)
          : [...estados, estado],
      };
    });

  const guardar = async () => {
    setEnviando(true);
    const { ok, titulo } = await guardarPermisosGestor(gestor.id, permisos);
    onMensaje(titulo, ok ? "success" : "info");
    if (!ok) {
      setEnviando(false);
      return;
    }
    onGuardado();
  };

  return (
    <ModalSolicitud
      titulo="¡ Permisos !"
      ancho="sm"
      onCerrar={onCerrar}
      acciones={
        <Button color="primary" disabled={enviando} onClick={guardar}>
          {enviando ? "Guardando..." : "Guardar"}
        </Button>
      }
    >
      <Typography variant="subtitle2">{gestor.nombre}</Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        {gestor.perfil?.nombre}
      </Typography>

      {tipos.map((tipo) => {
        const asignado = tipo.codigo in permisos;
        const estados = permisos[tipo.codigo] ?? [];
        return (
          <div key={tipo.codigo} style={{ marginTop: 16 }}>
            <Divider />
            <FormControlLabel
              control={
                <Checkbox
                  checked={asignado}
                  onChange={() => alternarTipo(tipo.codigo)}
                  color="primary"
                />
              }
              label={<strong>{tipo.nombre}</strong>}
            />
            {asignado && (
              <FormGroup row style={{ paddingLeft: 32 }}>
                {tipo.estados.map((estado) => (
                  <FormControlLabel
                    key={estado.codigo}
                    control={
                      <Checkbox
                        size="small"
                        checked={estados.includes(estado.codigo)}
                        onChange={() => alternarEstado(tipo.codigo, estado.codigo)}
                      />
                    }
                    label={estado.nombre}
                  />
                ))}
              </FormGroup>
            )}
            {asignado && estados.length === 0 && (
              <Typography variant="caption" color="error" style={{ paddingLeft: 32 }}>
                Sin estados no verá ninguna solicitud de este tipo.
              </Typography>
            )}
          </div>
        );
      })}
    </ModalSolicitud>
  );
};

ModalPermisos.propTypes = {
  gestor: PropTypes.shape({
    id: PropTypes.number.isRequired,
    nombre: PropTypes.string,
    perfil: PropTypes.object,
    permisos: PropTypes.object,
  }).isRequired,
  tipos: PropTypes.arrayOf(
    PropTypes.shape({
      codigo: PropTypes.string.isRequired,
      nombre: PropTypes.string.isRequired,
      estados: PropTypes.arrayOf(
        PropTypes.shape({ codigo: PropTypes.string, nombre: PropTypes.string }),
      ).isRequired,
    }),
  ).isRequired,
  onCerrar: PropTypes.func.isRequired,
  onGuardado: PropTypes.func.isRequired,
  onMensaje: PropTypes.func.isRequired,
};

export default ModalPermisos;
